import React, {
  memo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import CroppedRow from '@lib/components/CroppedRow/CroppedRow';
import classNames from 'classnames';
import styles from './TabBar.module.scss';
import TabBarMoreItems from './TabBarMoreItems';
import {
  ActiveItem,
  TabBarItem as TabBarItemType,
  TabBarItemTypes,
  OFFSET_WIDTH,
  AFTER_TABS_WIDTH,
  INDICATOR_BASE_WIDTH,
} from './constants';
import TabBarItem from './TabBarItem';
import { isTabActive } from './utils/activeStateComparison';

type IndicatorRect = { left: number; width: number };

type IndicatorState = {
  rect: IndicatorRect | null;
  onMoreTrigger: boolean;
  // Mirrors the "More" trigger's own error state, which reflects *any* errored
  // item in the dropdown — not just the active one — so the indicator can be
  // painted in the same color as the static line it covers.
  moreTriggerHasError: boolean;
};

const EMPTY_INDICATOR: IndicatorState = {
  rect: null,
  onMoreTrigger: false,
  moreTriggerHasError: false,
};

function measureActiveIndicator(
  container: HTMLElement | null,
  items: TabBarItemType[],
  active?: ActiveItem,
): IndicatorState {
  // `''` is a legitimate active value (isTabActive treats an empty-string tab
  // as active), so only bail on a genuinely absent one.
  if (!container || active === undefined || active === null) {
    return EMPTY_INDICATOR;
  }

  // A tab's `value` can carry a query string (e.g. list tabs with default
  // pagination params) while `active` is often a bare pathname, so look the
  // item up the same way TabBarItem/isTabActive decide highlighting instead
  // of comparing `active` against data-id directly.
  const activeItem = items.find((item) => isTabActive(item.value, active));
  if (!activeItem) return EMPTY_INDICATOR;

  const escapedId =
    typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(activeItem.value)
      : activeItem.value;
  // A cropped tab is rendered twice with the same data-id: once inside the
  // "More" dropdown (first in document order) and once as the top-level
  // button. The dropdown hides via `visibility: hidden`, so its copy keeps a
  // nonzero offsetWidth and reports offsets relative to the absolutely
  // positioned dropdown — skip it so cropped tabs fall through to the More
  // trigger below instead of positioning the indicator from dropdown coords.
  const activeEl = Array.from(
    container.querySelectorAll<HTMLElement>(`[data-id="${escapedId}"]`),
  ).find((el) => !el.closest(`.${styles.moreWrapper}`));

  if (activeEl && activeEl.offsetWidth > 0) {
    return {
      rect: { left: activeEl.offsetLeft, width: activeEl.offsetWidth },
      onMoreTrigger: false,
      moreTriggerHasError: false,
    };
  }

  // Active tab is currently cropped into the "More" dropdown — point the
  // indicator at the trigger button instead of hiding it outright.
  const moreTrigger = container.querySelector<HTMLElement>('[data-id="more"]');
  if (moreTrigger?.classList.contains(styles.active)) {
    return {
      rect: { left: moreTrigger.offsetLeft, width: moreTrigger.offsetWidth },
      onMoreTrigger: true,
      moreTriggerHasError: moreTrigger.classList.contains(styles.hasError),
    };
  }

  return EMPTY_INDICATOR;
}

export type TabBarVariant = 'default' | 'fullWidth';

type Props = {
  active?: ActiveItem;
  className?: string;
  items: TabBarItemType[];
  onActiveChange: (item: TabBarItemType) => void;
  withoutCrop?: boolean;
  useSidePaddings?: boolean;
  /** Cancels a 32px-padded parent (e.g. PageBody) with a matching negative
   * margin so the border-bottom spans edge-to-edge; pair with
   * `useSidePaddings` to keep the tabs themselves visually aligned. */
  bleedSidePaddings?: boolean;
  useScrollIntoView?: boolean;
  renderAfterTabs?: React.ReactNode;
  /** Variant style: 'default' or 'fullWidth' (centered items, taller height, no border) */
  variant?: TabBarVariant;
};

function TabBar({
  active,
  className,
  useSidePaddings,
  bleedSidePaddings,
  items,
  onActiveChange,
  withoutCrop,
  useScrollIntoView,
  renderAfterTabs,
  variant = 'default',
}: Props) {
  const isFullWidth = variant === 'fullWidth';
  // Reserve space for "More" button + afterTabs when present
  const afterTabsOffset = renderAfterTabs ? AFTER_TABS_WIDTH : 0;

  // Measure available width from outerWrapper (has proper width constraint)
  const outerWrapperRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const element = outerWrapperRef.current;
    if (!element) return undefined;

    // Initial measurement
    const measureWidth = () => {
      const { width } = element.getBoundingClientRect();
      setMaxWidth((prev) => (prev !== width ? width : prev));
    };
    measureWidth();

    // Watch for resize changes
    const resizeObserver = new ResizeObserver(() => {
      measureWidth();
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []); // Only set up observer once

  const [indicator, setIndicator] = useState<IndicatorState>(EMPTY_INDICATOR);

  // Some consumers mount a fresh TabBar instance per tab (a different
  // component tree per route rather than one persistent instance with
  // swapped content), so `indicator` restarts at `rect: null` on every
  // click. Without this, that first post-mount measurement would animate
  // in from left:0/scaleX(0) — skip the transition for that one commit,
  // then re-arm it so genuine position changes on an already-mounted
  // instance keep animating normally.
  const canAnimateIndicatorRef = useRef(false);

  useEffect(() => {
    if (indicator.rect) {
      canAnimateIndicatorRef.current = true;
    }
  }, [indicator.rect]);

  useLayoutEffect(() => {
    const container = outerWrapperRef.current;
    if (!container) return undefined;

    const remeasure = () => {
      const next = measureActiveIndicator(container, items, active);
      setIndicator((prev) =>
        prev.rect?.left === next.rect?.left &&
        prev.rect?.width === next.rect?.width &&
        prev.onMoreTrigger === next.onMoreTrigger &&
        prev.moreTriggerHasError === next.moreTriggerHasError
          ? prev
          : next,
      );
    };

    remeasure();

    // Re-measure whenever CroppedRow toggles which items are cropped/visible,
    // since that can shift the active tab's position without changing
    // `active`, `items`, or `maxWidth`.
    const mutationObserver = new MutationObserver(remeasure);
    mutationObserver.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    return () => mutationObserver.disconnect();
  }, [active, items, maxWidth]);

  const activeItem = items.find((item) => isTabActive(item.value, active));
  // 'blue' has no modifier class — it's the indicator's default color.
  const activeItemColorType =
    activeItem?.type && activeItem.type !== TabBarItemTypes.blue
      ? activeItem.type
      : undefined;
  // On the "More" trigger the indicator covers that button's own static line,
  // which is red whenever *any* cropped item has an error — so take the error
  // state from the trigger rather than from the active item, or the blue
  // indicator would paint over (and hide) the red line.
  const indicatorHasError = indicator.onMoreTrigger
    ? indicator.moreTriggerHasError
    : activeItem?.hasError;
  const indicatorClassName = classNames(styles.activeIndicator, {
    [styles.visible]: !!indicator.rect,
    [styles.hasError]: indicatorHasError,
    ...(activeItemColorType ? { [styles[activeItemColorType]]: true } : {}),
  });
  // scaleX on a fixed-width box (see .activeIndicator) instead of animating
  // `width` directly — see comment in TabBar.module.scss. The base width is
  // set here rather than in the stylesheet so the divisor below can't drift
  // away from the box it scales.
  const indicatorStyle: React.CSSProperties = {
    width: INDICATOR_BASE_WIDTH,
    ...(indicator.rect
      ? {
          transform: `translateX(${indicator.rect.left}px) scaleX(${
            indicator.rect.width / INDICATOR_BASE_WIDTH
          })`,
          transitionDuration: canAnimateIndicatorRef.current ? undefined : '0s',
        }
      : null),
  };

  return (
    <div
      ref={outerWrapperRef}
      className={classNames(
        styles.outerWrapper,
        {
          [styles.outerWrapperWithSidePaddings]: useSidePaddings,
          [styles.bleedSidePaddings]: bleedSidePaddings,
          [styles.fullWidth]: isFullWidth,
        },
        'TabBarComponent',
        className,
      )}
    >
      <div className={styles.wrapper}>
        <CroppedRow
          className={styles.root}
          items={items}
          offsetWidth={OFFSET_WIDTH + afterTabsOffset}
          maxWidth={maxWidth}
          renderItem={({ key, ...restItemProps }) => (
            <TabBarItem
              key={key}
              active={active}
              onActiveChange={onActiveChange}
              useScrollIntoView={useScrollIntoView}
              isFullWidth={isFullWidth}
              withoutStaticActiveLine
              {...restItemProps}
            />
          )}
          renderMoreItems={(renderMoreProps) => (
            <TabBarMoreItems
              active={active}
              onActiveChange={onActiveChange}
              withoutStaticActiveLine
              {...renderMoreProps}
            />
          )}
          withoutCrop={withoutCrop}
          renderAfter={
            <>
              <span
                className={indicatorClassName}
                style={indicatorStyle}
                aria-hidden="true"
              />
              {renderAfterTabs && (
                <div className={styles.afterTabs}>{renderAfterTabs}</div>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

export default memo(TabBar);
